import { useQuery } from "@apollo/client";
import { BoundingBox } from "contexts/MapViewConfigurationContext/hooks/useBoundingBox";
import useMapViewConfiguration from "contexts/MapViewConfigurationContext/hooks/useMapViewConfiguration";
import { useMapViewRoutingMetadata } from "contexts/RoutingMetadataContext";
import { useScreeningViewContext } from "contexts/ScreeningViewContext/ScreeningViewContext";
import {
    GetBusesWithCapacityCostQuery,
    GetBusesWithCapacityCostQueryResult,
    GetBusesWithCapacityCostQueryVariables
} from "generated/graphql";
import { loader } from "graphql.macro";
import {
    maybeHideLockedBusesFilter,
    maybeOwnersFilter,
    maybeScreeningViewStatesFilter
} from "graphql/helpers/busHelpers";
import { getCapacityCostsWhereClauseWithScopeView } from "graphql/helpers/capacityCostsWhereClause";
import { ColorMode, LocationFilter } from "types/busFilterTypes";
import {
    ALL_LOCATION_CODES,
    convertHasuraBusesToNiraBusesAndSplitBasedOnAuth,
    HIGH_CONFIDENCE_LOCATION_CODES,
    isOutOfRegionBus,
    ScopedBus,
    UnauthorizedBus
} from "types/busType";
import { RegionType } from "types/regionType";
import { Voltage } from "types/voltageTypes";
import { isPil } from "utils/pilUtils";
import usePlannedInterconnectionLocations from "./usePlannedInterconnectionLocations";

const GET_BUSES_WITH_CAPACITY_COST = loader(
    "src/graphql/getBusesWithCapacityCost.graphql"
);

interface BusesContext {
    readonly scopedBuses: readonly ScopedBus[];
    readonly isLoading: boolean;
}

const useBusesWithCapacityCostsVariables = (
    boundingBoxOverride?: BoundingBox,
    voltagesOverride?: readonly Voltage[]
): GetBusesWithCapacityCostQueryVariables => {
    const { region, scopeView } = useMapViewRoutingMetadata();
    const {
        busFiltersConfiguration: { busFilters },
        boundingBoxConfiguration: { boundingBox }
    } = useMapViewConfiguration();
    const maybeScreeningViewData = useScreeningViewContext();
    const queryBoundingBox = boundingBoxOverride ?? boundingBox;
    const queryVoltages = voltagesOverride ?? busFilters.voltages;

    const capacityCostsWhereclause = getCapacityCostsWhereClauseWithScopeView({
        scope: busFilters.scope,
        scopeView: scopeView,
        maybeMaxAllocatedCosts: busFilters.maybeMaxAllocatedCosts.enabled
            ? busFilters.maybeMaxAllocatedCosts.maxCosts
            : undefined,
        maybeMaxTotalCosts: busFilters.maybeMaxTotalCosts.enabled
            ? busFilters.maybeMaxTotalCosts.maxCosts
            : undefined,
        includePreExistingConstraints: busFilters.includePreExistingConstraints
    });

    const includeConstraintCount =
        region === RegionType.CAISO &&
        busFilters.colorMode === ColorMode.CONSTRAINT_COUNT;

    const constraintStacksWhereClause = includeConstraintCount
        ? {
              injection_threshold: { _eq: 0 },
              scope: { _eq: busFilters.scope },
              scope_view: { _eq: scopeView }
          }
        : undefined;

    return {
        ...queryBoundingBox,
        voltages: [...queryVoltages],
        scope: busFilters.scope,
        scopeView: scopeView,
        capacity_costs_where_clause: capacityCostsWhereclause,
        region,
        filter_locked_buses_where_clause: maybeHideLockedBusesFilter(
            busFilters.hideLockedBuses
        ),
        locationCodes: busFilters.hideLowConfidenceBuses
            ? HIGH_CONFIDENCE_LOCATION_CODES
            : ALL_LOCATION_CODES,
        filter_states_where_clause: maybeScreeningViewStatesFilter(
            maybeScreeningViewData
        ),
        includeConstraintCount: includeConstraintCount,
        constraint_stacks_where_clause: constraintStacksWhereClause,
        filter_owners_where_clause: maybeOwnersFilter(busFilters.owners)
    };
};

const useBusData = (): BusesContext => {
    const variables = useBusesWithCapacityCostsVariables();
    const {
        busFiltersConfiguration: { busFilters }
    } = useMapViewConfiguration();
    const { componentConfig } = useMapViewRoutingMetadata();

    const activeDataQuery = useQuery(GET_BUSES_WITH_CAPACITY_COST, {
        variables: variables,
        fetchPolicy: "no-cache"
    });

    const pilPlannedMwByNiraBusId = usePlannedInterconnectionLocations(
        componentConfig.hasPlannedInterconnectionLocations
    );

    const [allScopedBuses] =
        convertQueryResultAndSplitIntoScopedAndUnauthorizedBuses(
            activeDataQuery
        );

    const scopedBusesWithPlannedMw =
        pilPlannedMwByNiraBusId === undefined ||
        pilPlannedMwByNiraBusId.size === 0
            ? allScopedBuses
            : allScopedBuses.map((scopedBus: ScopedBus) => {
                  const plannedMw = pilPlannedMwByNiraBusId.get(
                      scopedBus.bus.niraBusId
                  );
                  return plannedMw === undefined
                      ? scopedBus
                      : {
                            ...scopedBus,
                            bus: { ...scopedBus.bus, plannedMw }
                        };
              });

    // Until the PIL list loads, a bus is neither confirmed planned nor
    // unplanned, so an active PIL/UPIL filter matches nothing rather than
    // showing unverified results.
    const pilFilteredScopedBuses =
        componentConfig.hasPlannedInterconnectionLocations &&
        busFilters.locationFilter !== undefined &&
        busFilters.locationFilter !== LocationFilter.ALL
            ? pilPlannedMwByNiraBusId === undefined
                ? []
                : scopedBusesWithPlannedMw.filter((b: ScopedBus) => {
                      const isBusPil = isPil(b.bus.plannedMw);
                      return busFilters.locationFilter === LocationFilter.PIL
                          ? isBusPil
                          : !isBusPil;
                  })
            : scopedBusesWithPlannedMw;

    // Region-specific (WECC/RTOE): when the "Hide RTOE buses" toggle is on, drop
    // out-of-region buses client-side. They keep their real location_code, so
    // they are fetched normally and can only be removed here (see isOutOfRegionBus).
    const scopedBuses = busFilters.hideOutOfRegionBuses
        ? pilFilteredScopedBuses.filter(
              (b: ScopedBus) => !isOutOfRegionBus(b.bus)
          )
        : pilFilteredScopedBuses;

    const isLoading =
        activeDataQuery.loading ||
        (activeDataQuery.previousData === undefined &&
            activeDataQuery.data === undefined);

    return {
        scopedBuses,
        isLoading
    };
};

function convertQueryResultAndSplitIntoScopedAndUnauthorizedBuses(
    queryResult: GetBusesWithCapacityCostQueryResult
): readonly [ReadonlyArray<ScopedBus>, ReadonlyArray<UnauthorizedBus>] {
    const currentData: GetBusesWithCapacityCostQuery | undefined =
        queryResult.data;

    if (currentData === undefined) {
        if (queryResult.previousData === undefined) {
            // This means that this is the first time this is ever called
            return [[], []];
        } else {
            return convertHasuraBusesToNiraBusesAndSplitBasedOnAuth(
                queryResult.previousData.buses
            );
        }
    } else {
        return convertHasuraBusesToNiraBusesAndSplitBasedOnAuth(
            currentData.buses
        );
    }
}

export {
    useBusData,
    useBusesWithCapacityCostsVariables,
    convertQueryResultAndSplitIntoScopedAndUnauthorizedBuses
};
